import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { LoadingButton } from '../buttons/LoadingButton';
import { GradientBorder } from 'src/components/gradients/GradientBorder';
import { GradientTypography } from 'src/components/gradients/GradientTypography';
import { useState } from 'react';
import { OnRamperDialog } from '../payment/OnRamperDialog';
import { useAuth } from 'src/contexts/AuthContext';
import { CryptoChain } from 'src/util/CryptoChain';
import { SignInGuard } from '../guards/SignInGuard';
import { NotEnoughFunds } from './NotEnoughFunds';
import { ConnectWalletGuard } from '../guards/ConnectWalletGuard';
import { ConfirmMint } from './ConfirmMint';
import { useBalance, useConnect, useNetwork, useSwitchNetwork } from 'wagmi';
import { MetaMaskConnector } from 'wagmi/connectors/metaMask';
import { useAccountLowercase } from 'src/hooks/useAccountLowercase';
import { ImageOptimized } from '../image/ImageOptimized';

export type MintCardProps = {
  collectionTitle: string;
  supplyMinted: number;
  supplyTotal?: number;
  mintPrice: { price: number; currency: string };
  mintImage: string;
  unavailable?: boolean;
  contractChainId: number;
  eagerLoad?: boolean;
};

export function MintCard({
  collectionTitle,
  supplyMinted,
  supplyTotal,
  mintPrice,
  mintImage,
  unavailable = false,
  contractChainId,
  eagerLoad = false,
}: MintCardProps) {
  const { userData } = useAuth();
  const { connectAsync } = useConnect();
  const { address, isDisconnected } = useAccountLowercase();
  const { chain } = useNetwork();
  const { switchNetwork } = useSwitchNetwork();
  const balance = useBalance({
    chainId: chain?.id,
    addressOrName: address,
    enabled: true,
  });
  const [onRamperSrc, setOnRamperSrc] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const [notEnoughFundsOpen, setNotEnoughFundsOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const loadRamperUrl = (cryptoCoin: string, address: string) => {
    //TODO: load onramper without moralis
    //to match primary.main
    //const newColor = response.data.replace('1d2d50', '4dc8ff');
    //const src = `${newColor}}&defaultCrypto=${cryptoCoin}&wallets=${cryptoCoin}:${address}&darkMode=true`;
    const src = `$defaultCrypto=${cryptoCoin}&wallets=${cryptoCoin}:${address}&darkMode=true`;
    setOnRamperSrc(src);
  };
  const handleOnRamper = async () => {
    if (!userData?.addresses) {
      return;
    }
    const coin = CryptoChain.from(chain?.id).coin;
    //TODO: somehow read from preferred wallet address
    loadRamperUrl(coin, address as string);
    setDialogOpen(true);
  };
  const handleMint = async () => {
    if (!mintPrice) {
      return;
    }
    if (!userData) {
      return setSignInModalOpen(true);
    }
    if (isDisconnected) {
      await connectAsync({
        connector: new MetaMaskConnector(),
      });
    }
    if (!chain?.id || !userData?.addresses?.length) {
      return setWalletModalOpen(true);
    }
    if (parseInt(chain?.id?.toString(), 16) !== contractChainId) {
      switchNetwork?.(contractChainId);
    }
    if (Number(balance?.data?.value) <= mintPrice.price) {
      return setNotEnoughFundsOpen(true);
    }
    setConfirmationModalOpen(true);
  };
  const theme = useTheme();
  return (
    <>
      {confirmationModalOpen && (
        <ConfirmMint
          open={confirmationModalOpen}
          handleClose={() => {
            setConfirmationModalOpen(false);
          }}
          title={collectionTitle}
          chainId={contractChainId}
          mintPrice={mintPrice}
        />
      )}
      {walletModalOpen && (
        <ConnectWalletGuard
          onClose={() => {
            setWalletModalOpen(false);
          }}
          open={walletModalOpen}
        />
      )}
      {notEnoughFundsOpen && (
        <NotEnoughFunds
          coin={mintPrice.currency}
          handleOnRamper={handleOnRamper}
          setOpen={setNotEnoughFundsOpen}
          open={notEnoughFundsOpen}
          handleClose={() => {
            return setNotEnoughFundsOpen(false);
          }}
        />
      )}
      {dialogOpen && (
        <OnRamperDialog
          onRamperSrc={onRamperSrc}
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
        />
      )}
      {signInModalOpen && (
        <SignInGuard
          title={'Mint this item?'}
          open={signInModalOpen}
          onClose={() => {
            return setSignInModalOpen(false);
          }}
        />
      )}
      <Box
        sx={{
          width: '360px',
          py: 4,
        }}
      >
        <GradientBorder
          backgroundColor={theme.palette.background.elevation[12]}
          borderRadius="10px"
          sx={{
            width: '100%',
            boxShadow: theme.shadows[4],
            '&:hover': {
              boxShadow: theme.shadows[7],
            },
          }}
        >
          <Stack
            gap="12px"
            justifyContent="center"
            alignItems="center"
            width="100%"
            paddingX="8px"
            paddingY="8px"
          >
            <GradientBorder
              borderRadius="6px"
              sx={{
                height: '340px',
                width: '100%',
              }}
            >
              <ImageOptimized
                src={mintImage}
                layout="fill"
                alt="image"
                style={{
                  height: '413px',
                  width: '100%',
                }}
                loading={eagerLoad ? 'eager' : 'lazy'}
              />
            </GradientBorder>
            <GradientTypography
              gradientColor="primary.vertical"
              variant="h5"
              sx={{ textTransform: 'uppercase', mt: '-8px' }}
            >
              {collectionTitle}
            </GradientTypography>
            <Stack
              direction="row"
              gap="12px"
              width="100%"
              justifyContent="center"
            >
              <Stack
                sx={{
                  backgroundColor: '#00357A',
                  boxShadow: theme.shadowsHard.emboss,
                  backdropFilter: 'blur(2.5px)',
                  borderRadius: '4px',
                  textAlign: 'center',
                  padding: '8px',
                  width: '50%',
                }}
              >
                {supplyTotal && (
                  <GradientTypography
                    gradientColor="primary.vertical"
                    variant="h5"
                    sx={{ textTransform: 'uppercase' }}
                  >
                    {supplyTotal - supplyMinted} Left
                  </GradientTypography>
                )}
                <Typography
                  sx={{ textTransform: 'uppercase' }}
                  variant="subtitle1"
                >
                  Out Of {supplyTotal}
                </Typography>
              </Stack>
              <Stack
                justifyContent="center"
                sx={{
                  backgroundColor: '#00357A',
                  boxShadow: theme.shadowsHard.emboss,
                  backdropFilter: 'blur(2.5px)',
                  borderRadius: '4px',
                  textAlign: 'center',
                  paddingX: '8px',
                  width: '50%',
                }}
              >
                <GradientTypography
                  gradientColor="secondary.vertical"
                  variant="h5"
                >
                  {mintPrice.price / 10 ** 18} {mintPrice.currency}
                </GradientTypography>
                <Typography
                  sx={{ textTransform: 'uppercase' }}
                  variant="subtitle1"
                >
                  Mint Price
                </Typography>
              </Stack>
            </Stack>
            <Box
              sx={{
                width: '100%',
                borderRadius: '4px',
              }}
            >
              <LoadingButton
                onClick={handleMint}
                sx={{ width: '100%' }}
                color={unavailable ? 'inherit' : undefined}
                disabled={unavailable}
                variant="contained"
              >
                {unavailable ? 'Unavailable' : 'Mint Now'}
              </LoadingButton>
            </Box>
          </Stack>
        </GradientBorder>
      </Box>
    </>
  );
}
